package info.rezident.app.ui.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import info.rezident.app.core.AppTracking
import info.rezident.app.data.user.UserRepository
import info.rezident.app.ui.theme.AppTheme
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

/**
 * Sticky bottom CTA bar for mobile that appears when scrolling away from the paywall strip
 * Shows pricing and CTA to unlock recommendations
 *
 * @param isVisible Whether the sticky bar should be visible
 * @param priceText The price to display (e.g., "24.99 RON")
 * @param onClick Callback when the CTA is clicked
 * @param modifier Additional modifiers to apply
 */
@Composable
fun StickyBottomCTA(
    isVisible: Boolean,
    priceText: String?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    var shouldRender by remember { mutableStateOf(false) }
    var isAnimating by remember { mutableStateOf(false) }
    var useShortText by remember { mutableStateOf(false) }
    val userProfile = UserRepository.user.value
    val academicYear = userProfile?.academicYear ?: "unknown"
    val priceVariant = userProfile?.priceVariant?.name ?: "unknown"

    LaunchedEffect(isVisible) {
        if (isVisible) {
            shouldRender = true
            delay(50) // Small delay before animation starts
            isAnimating = true
        } else {
            isAnimating = false
            delay(300) // Wait for slide-out animation
            shouldRender = false
        }
    }

    // Detect if we need to use short text based on viewport width
    LaunchedEffect(priceText) {
        val fullText = "Deblochează Analiza Detaliată${if (priceText != null) " $priceText" else ""}"

        // Estimate if text will overflow based on viewport width
        // Rough calculation: each character ~8-10px, padding 20px, margins 48px
        val viewportWidth = window.innerWidth
        val availableWidth = viewportWidth - 48 // Account for horizontal padding (24px each side)
        val estimatedTextWidth = fullText.length * 9 // Average character width

        useShortText = estimatedTextWidth > availableWidth
    }

    if (shouldRender) {
        val bgColor = AppTheme.colors.background
        val accentColor = AppTheme.colors.accent
        val onAccentColor = AppTheme.colors.onAccent

        Column(
            modifier = modifier
                .position(Position.Fixed)
                .bottom(0.px)
                .left(0.px)
                .padding(top = 8.px, leftRight = 24.px, bottom = 8.px)
                .width(100.percent)
                .zIndex(998)
                .styleModifier {
                    property("backdrop-filter", "blur(10px)")
                    property(
                        "transform",
                        if (isAnimating) "translateY(0)" else "translateY(100%)"
                    )
                    property(
                        "transition",
                        "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)"
                    )
                    property(
                        "background",
                        "linear-gradient(180deg, ${bgColor}00 0%, $bgColor 30%)"
                    )
                    property("box-shadow", "0 -2px 16px rgba(0, 0, 0, 0.1)")
                }
        ) {
            // CTA Button
            val darkMode = ColorMode.current.isDark
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .backgroundColor(accentColor)
                    .borderRadius(AppTheme.shapes.mediumRound)
                    .padding(leftRight = 10.px, topBottom = 8.px)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        AppTracking.logEvent(
                            AppTracking.EVENT_RECOMMENDATIONS_STICKY_CTA_CLICK,
                            mapOf(
                                AppTracking.PARAM_TEMPLATE_ID to 1,
                                AppTracking.PARAM_ACADEMIC_YEAR to academicYear,
                                AppTracking.PARAM_PRICE_VARIANT to priceVariant
                            )
                        )
                        onClick()
                    }
                    .styleModifier {
                        property(
                            "box-shadow",
                            "0 4px 6px ${if (darkMode) "rgba(80, 80, 80, 1)" else "rgba(128, 128, 128, 1)"}"
                        )
                    }
            ) {
                // Main text with price
                Row(
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    val mainText = if (useShortText) "Deblochează Analiza" else "Deblochează Analiza Detaliată"

                    SpanText(
                        text = mainText,
                        modifier = AppTheme.typography.body1Medium.toModifier()
                            .color(onAccentColor)
                            .whiteSpace(WhiteSpace.NoWrap)
                    )
                    if (priceText != null) {
                        SpanText(
                            text = " $priceText",
                            modifier = AppTheme.typography.body1Medium.toModifier()
                                .color(onAccentColor)
                                .whiteSpace(WhiteSpace.NoWrap)
                        )
                    }
                }
            }

            Spacer(Modifier.height(8.px))

            // Subtext
            SpanText(
                text = "Fără abonament • Refund în 14 zile",
                modifier = AppTheme.typography.body2.toModifier()
                    .color(AppTheme.colors.textTertiary)
                    .opacity(0.9)
                    .fillMaxWidth()
                    .textAlign(TextAlign.Center)
            )
        }
    }
}
